import React, { useState, useEffect, useMemo } from "react";
import {
  Send,
  CheckCircle2,
  FileText,
  FileCheck2,
  Award,
  DollarSign,
  Sliders,
  Printer,
  Copy,
  Download,
  ShieldCheck,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Trophy,
} from "lucide-react";
import {
  Opportunity,
  Requirement,
  DraftSection,
  RequiredForm,
  FormatRule,
  UserProfile,
} from "../../types";
import { subscribeToRequirements } from "../../services/requirementsService";
import { subscribeToDraftSections } from "../../services/draftsService";
import {
  subscribeToRequiredForms,
  subscribeToFormatRules,
} from "../../services/requirementsService";
import {
  auditSubmissionPackage,
  recordProposalSignOff,
  revokeProposalSignOff,
} from "../../services/submissionService";
import {
  compileMasterProposalHtml,
  compileMasterProposalMarkdown,
  downloadCompiledDocument,
  sortRequirementsInProposalOrder,
} from "../../services/submissionExportService";
import { RecordSubmissionModal } from "./submission/RecordSubmissionModal";

export interface SubmitTabProps {
  opportunity: Opportunity;
  user: UserProfile | null;
  onSwitchTab?: (tab: string) => void;
  onOpenDebrief?: () => void;
}

export const SubmitTab: React.FC<SubmitTabProps> = ({
  opportunity,
  user,
  onSwitchTab,
  onOpenDebrief,
}) => {
  const [requirements, setRequirements] = useState<Requirement[]>([]);
  const [draftSections, setDraftSections] = useState<DraftSection[]>([]);
  const [forms, setForms] = useState<RequiredForm[]>([]);
  const [formatRules, setFormatRules] = useState<FormatRule[]>([]);
  const [expandedCheckId, setExpandedCheckId] = useState<string | null>(null);

  const [isRecordModalOpen, setIsRecordModalOpen] = useState(false);
  const [leadComments, setLeadComments] = useState("");
  const [execComments, setExecComments] = useState("");
  const [isSigningLead, setIsSigningLead] = useState(false);
  const [isSigningExec, setIsSigningExec] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  // Subscriptions
  useEffect(() => {
    const unsub = subscribeToRequirements(opportunity.id, setRequirements);
    return () => unsub();
  }, [opportunity.id]);

  useEffect(() => {
    const unsub = subscribeToDraftSections(opportunity.id, setDraftSections);
    return () => unsub();
  }, [opportunity.id]);

  useEffect(() => {
    const unsub = subscribeToRequiredForms(opportunity.id, setForms);
    return () => unsub();
  }, [opportunity.id]);

  useEffect(() => {
    const unsub = subscribeToFormatRules(opportunity.id, setFormatRules);
    return () => unsub();
  }, [opportunity.id]);

  // Compute live pre-flight audit
  const audit = useMemo(() => {
    return auditSubmissionPackage({
      opportunity,
      requirements,
      draftSections,
      forms,
      formatRules,
    });
  }, [opportunity, requirements, draftSections, forms, formatRules]);

  const sortedReqs = useMemo(() => {
    return sortRequirementsInProposalOrder(requirements);
  }, [requirements]);

  const sectionMap = useMemo(() => {
    const map = new Map<string, DraftSection>();
    draftSections.forEach((s) => map.set(s.requirementId, s));
    return map;
  }, [draftSections]);

  const totalWords = useMemo(() => {
    return draftSections.reduce((sum, s) => sum + (s.wordCount || 0), 0);
  }, [draftSections]);

  const estimatedPages = Math.max(1, Math.ceil(totalWords / 450));
  const isSubmitted = opportunity.stage === "submitted" || Boolean(opportunity.officialSubmission);

  // Handle Lead Sign-Off
  const handleLeadSignOff = async () => {
    if (!user) return;
    setIsSigningLead(true);
    try {
      await recordProposalSignOff(opportunity.id, "lead", user, leadComments);
      setLeadComments("");
    } catch (err) {
      console.error("Failed lead sign-off:", err);
    } finally {
      setIsSigningLead(false);
    }
  };

  // Handle Exec Sign-Off
  const handleExecSignOff = async () => {
    if (!user) return;
    setIsSigningExec(true);
    try {
      await recordProposalSignOff(opportunity.id, "executive", user, execComments);
      setExecComments("");
    } catch (err) {
      console.error("Failed executive sign-off:", err);
    } finally {
      setIsSigningExec(false);
    }
  };

  // Revoke Sign-Off
  const handleRevokeSignOff = async (role: "lead" | "executive") => {
    try {
      await revokeProposalSignOff(opportunity.id, role);
    } catch (err) {
      console.error(`Failed revoking ${role} sign-off:`, err);
    }
  };

  // Copy HTML
  const handleCopyHtml = async () => {
    const html = compileMasterProposalHtml({ opportunity, requirements, draftSections });
    try {
      await navigator.clipboard.writeText(html);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2500);
    } catch (err) {
      console.error("Failed copying proposal HTML:", err);
    }
  };

  // Download Markdown
  const handleDownloadMarkdown = () => {
    const md = compileMasterProposalMarkdown({ opportunity, requirements, draftSections });
    const slug = opportunity.title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .slice(0, 30);
    downloadCompiledDocument(`MLC_Proposal_${slug}.md`, md, "text/markdown;charset=utf-8");
  };

  // Download / Print HTML
  const handlePrintDocument = () => {
    const html = compileMasterProposalHtml({ opportunity, requirements, draftSections });
    const printWindow = window.open("", "_blank");
    if (printWindow) {
      printWindow.document.open();
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
      }, 350);
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case "narrative":
        return <FileText className="h-4 w-4" />;
      case "forms":
        return <FileCheck2 className="h-4 w-4" />;
      case "certifications":
        return <Award className="h-4 w-4" />;
      case "pricing":
        return <DollarSign className="h-4 w-4" />;
      case "format_rules":
        return <Sliders className="h-4 w-4" />;
      default:
        return <FileText className="h-4 w-4" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Top Banner & Readiness Scorecard */}
      <div className="rounded-2xl border border-slate-200/80 bg-white p-6 shadow-2xs dark:border-slate-800 dark:bg-slate-900">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between border-b border-slate-100 pb-5 dark:border-slate-800">
          <div>
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-bold uppercase tracking-wider text-[#2ba5b5]">
                Stage 6 · Final Submission Package
              </span>
              <span className="rounded-full bg-teal-50 px-2 py-0.5 text-[10px] font-semibold text-[#2ba5b5] dark:bg-teal-950/40">
                Due: {opportunity.dueDate}
              </span>
            </div>
            <h2 className="mt-1 text-2xl font-bold tracking-tight text-slate-900 dark:text-white">
              Submission & Sign-Off Gate
            </h2>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Audit final package compliance across all 5 dimensions, execute dual leadership
              sign-offs, and record official submission.
            </p>
          </div>

          {/* Action Button: Record Submission */}
          <div className="flex items-center gap-2">
            {!isSubmitted ? (
              <button
                type="button"
                onClick={() => setIsRecordModalOpen(true)}
                className="btn-animate inline-flex items-center gap-2 rounded-xl bg-[#2ba5b5] px-5 py-2.5 text-xs font-bold text-white shadow-md hover:bg-[#238a97]"
              >
                <Send className="h-4 w-4" />
                <span>Record Official Submission</span>
              </button>
            ) : (
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center gap-1.5 rounded-xl border border-emerald-300 bg-emerald-50 px-3.5 py-2 text-xs font-bold text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  <span>Officially Submitted</span>
                </span>
                {onOpenDebrief && (
                  <button
                    type="button"
                    onClick={onOpenDebrief}
                    className="btn-animate inline-flex items-center gap-1.5 rounded-xl border border-purple-200 bg-purple-50 px-3.5 py-2 text-xs font-bold text-purple-700 hover:bg-purple-100 dark:border-purple-800 dark:bg-purple-950/40 dark:text-purple-300"
                  >
                    <Trophy className="h-4 w-4" />
                    <span>Proceed to Debrief (Stage 7)</span>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Readiness Meter & Metric Pills */}
        <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-5">
          <div className="rounded-xl border border-slate-100 bg-slate-50/50 p-3 text-center dark:border-slate-800 dark:bg-slate-800/40">
            <span className="text-[10px] font-semibold uppercase text-slate-400">
              Readiness Score
            </span>
            <div className="mt-1 text-2xl font-black text-slate-900 dark:text-white">
              {audit.overallScore}%
            </div>
            <div className="mt-0.5 text-[11px] font-medium text-slate-500">
              {audit.canSubmit ? "Ready for Submission" : "Blocked by Open Items"}
            </div>
          </div>

          <div className="rounded-xl border border-slate-100 bg-slate-50/50 p-3 text-center dark:border-slate-800 dark:bg-slate-800/40">
            <span className="text-[10px] font-semibold uppercase text-slate-400">Blockers</span>
            <div
              className={`mt-1 text-2xl font-black ${
                audit.blockersCount > 0 ? "text-rose-600" : "text-emerald-600"
              }`}
            >
              {audit.blockersCount}
            </div>
            <div className="mt-0.5 text-[11px] font-medium text-slate-500">
              {audit.blockersCount === 0 ? "Zero Blockers" : "Must Resolve Before Dispatch"}
            </div>
          </div>

          <div className="rounded-xl border border-slate-100 bg-slate-50/50 p-3 text-center dark:border-slate-800 dark:bg-slate-800/40">
            <span className="text-[10px] font-semibold uppercase text-slate-400">Warnings</span>
            <div className="mt-1 text-2xl font-black text-amber-600">{audit.warningsCount}</div>
            <div className="mt-0.5 text-[11px] font-medium text-slate-500">Advisory Items</div>
          </div>

          <div className="rounded-xl border border-slate-100 bg-slate-50/50 p-3 text-center dark:border-slate-800 dark:bg-slate-800/40">
            <span className="text-[10px] font-semibold uppercase text-slate-400">
              Estimated Pages
            </span>
            <div className="mt-1 text-2xl font-black text-[#2ba5b5]">~{estimatedPages}</div>
            <div className="mt-0.5 text-[11px] font-medium text-slate-500">
              {totalWords.toLocaleString()} total words
            </div>
          </div>

          <div className="rounded-xl border border-slate-100 bg-slate-50/50 p-3 text-center dark:border-slate-800 dark:bg-slate-800/40">
            <span className="text-[10px] font-semibold uppercase text-slate-400">
              Leadership Sign-Off
            </span>
            <div className="mt-1 text-sm font-bold text-slate-900 dark:text-white flex items-center justify-center gap-1.5">
              <span
                className={`h-2.5 w-2.5 rounded-full ${
                  opportunity.leadSignOff ? "bg-emerald-500" : "bg-slate-300 dark:bg-slate-600"
                }`}
                title="Lead Sign-Off"
              />
              <span
                className={`h-2.5 w-2.5 rounded-full ${
                  opportunity.executiveSignOff ? "bg-emerald-500" : "bg-slate-300 dark:bg-slate-600"
                }`}
                title="Executive Sign-Off"
              />
            </div>
            <div className="mt-1 text-[11px] font-medium text-slate-500">
              {opportunity.leadSignOff && opportunity.executiveSignOff
                ? "Fully Authorized"
                : opportunity.leadSignOff || opportunity.executiveSignOff
                  ? "Partially Signed"
                  : "Awaiting Signatures"}
            </div>
          </div>
        </div>
      </div>

      {/* Submitted Banner (If Stage is Submitted) */}
      {isSubmitted && opportunity.officialSubmission && (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50/70 p-5 shadow-2xs dark:border-emerald-900/60 dark:bg-emerald-950/30">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700 dark:bg-emerald-900/60 dark:text-emerald-300">
                <CheckCircle2 className="h-5 w-5" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-emerald-950 dark:text-emerald-200">
                  Proposal Officially Submitted via{" "}
                  {opportunity.officialSubmission.method.toUpperCase()}
                </h3>
                <p className="mt-0.5 text-xs text-emerald-800 dark:text-emerald-300">
                  Dispatched on{" "}
                  <strong>
                    {new Date(opportunity.officialSubmission.submittedAt).toLocaleString()}
                  </strong>{" "}
                  by {opportunity.officialSubmission.submittedBy} (
                  {opportunity.officialSubmission.submittedByEmail})
                </p>
                {opportunity.officialSubmission.confirmationNumber && (
                  <div className="mt-1 text-xs font-semibold text-emerald-900 dark:text-emerald-100">
                    Confirmation / Tracking #: {opportunity.officialSubmission.confirmationNumber}
                  </div>
                )}
                {opportunity.officialSubmission.notes && (
                  <p className="mt-1 text-xs italic text-emerald-700 dark:text-emerald-400">
                    &ldquo;{opportunity.officialSubmission.notes}&rdquo;
                  </p>
                )}
              </div>
            </div>

            {opportunity.officialSubmission.receiptDownloadUrl && (
              <a
                href={opportunity.officialSubmission.receiptDownloadUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-animate inline-flex items-center gap-1.5 rounded-lg border border-emerald-300 bg-white px-3 py-1.5 text-xs font-semibold text-emerald-800 shadow-2xs hover:bg-emerald-50 dark:border-emerald-700 dark:bg-slate-900 dark:text-emerald-300"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                <span>View Submission Receipt</span>
              </a>
            )}
          </div>
        </div>
      )}

      {/* Pre-Flight Compliance Audit Strip */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold uppercase tracking-wider text-slate-900 dark:text-white flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-[#2ba5b5]" />
            <span>Pre-Flight Compliance Audit (5 Dimensions)</span>
          </h3>
          <span className="text-xs text-slate-500">
            {audit.passedCount} passed · {audit.warningsCount} warnings · {audit.blockersCount}{" "}
            blockers
          </span>
        </div>

        <div className="grid gap-3">
          {audit.checks.map((check) => {
            const isExpanded = expandedCheckId === check.id;
            return (
              <div
                key={check.id}
                className="rounded-xl border border-slate-200/80 bg-white shadow-2xs dark:border-slate-800 dark:bg-slate-900"
              >
                <div
                  onClick={() => setExpandedCheckId(isExpanded ? null : check.id)}
                  className="flex cursor-pointer items-center justify-between p-4 hover:bg-slate-50/50 dark:hover:bg-slate-800/30"
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
                        check.status === "pass"
                          ? "bg-emerald-50 text-emerald-600 dark:bg-emerald-950/40"
                          : check.status === "warning"
                            ? "bg-amber-50 text-amber-600 dark:bg-amber-950/40"
                            : "bg-rose-50 text-rose-600 dark:bg-rose-950/40"
                      }`}
                    >
                      {getCategoryIcon(check.category)}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-slate-900 dark:text-white">
                          {check.label}
                        </span>
                        <span
                          className={`rounded-md px-1.5 py-0.2 text-[10px] font-bold uppercase ${
                            check.status === "pass"
                              ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300"
                              : check.status === "warning"
                                ? "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300"
                                : "bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300"
                          }`}
                        >
                          {check.status}
                        </span>
                      </div>
                      <p className="mt-0.5 text-xs text-slate-600 dark:text-slate-400">
                        {check.summary}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {check.actionTab && onSwitchTab && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onSwitchTab(check.actionTab!);
                        }}
                        className="btn-animate rounded-md border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                      >
                        {check.actionLabel || "Fix"}
                      </button>
                    )}
                    {isExpanded ? (
                      <ChevronDown className="h-4 w-4 text-slate-400" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-slate-400" />
                    )}
                  </div>
                </div>

                {isExpanded && check.details.length > 0 && (
                  <div className="border-t border-slate-100 bg-slate-50/50 p-4 dark:border-slate-800 dark:bg-slate-800/20">
                    <ul className="list-inside list-disc space-y-1 text-xs text-slate-600 dark:text-slate-400">
                      {check.details.map((d, idx) => (
                        <li key={idx}>{d}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Master Proposal Assembly & Preview Panel */}
      <div className="rounded-2xl border border-slate-200/80 bg-white p-6 shadow-2xs dark:border-slate-800 dark:bg-slate-900 space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between border-b border-slate-100 pb-4 dark:border-slate-800">
          <div>
            <h3 className="text-sm font-bold uppercase tracking-wider text-slate-900 dark:text-white flex items-center gap-2">
              <FileText className="h-4 w-4 text-[#2ba5b5]" />
              <span>Master Proposal Assembly</span>
            </h3>
            <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
              Compiled master response organized in formal solicitation outline sequence with front
              matter and appendices.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handlePrintDocument}
              className="btn-animate inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-2xs hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
              title="Print formatted proposal or save to PDF"
            >
              <Printer className="h-3.5 w-3.5 text-[#2ba5b5]" />
              <span>Print / Save as PDF</span>
            </button>

            <button
              type="button"
              onClick={handleCopyHtml}
              className="btn-animate inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-2xs hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
              title="Copy rich HTML to clipboard for pasting into Google Docs or Word"
            >
              <Copy className="h-3.5 w-3.5 text-[#2ba5b5]" />
              <span>{copySuccess ? "Copied!" : "Copy for Google Docs"}</span>
            </button>

            <button
              type="button"
              onClick={handleDownloadMarkdown}
              className="btn-animate inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-2xs hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
              title="Download clean Markdown document"
            >
              <Download className="h-3.5 w-3.5 text-[#2ba5b5]" />
              <span>Download (.MD)</span>
            </button>
          </div>
        </div>

        {/* Outline Breakdown Table */}
        <div className="overflow-x-auto rounded-xl border border-slate-100 dark:border-slate-800">
          <table className="w-full text-left text-xs">
            <thead className="bg-slate-50 text-[11px] font-bold uppercase text-slate-500 dark:bg-slate-800/60 dark:text-slate-400">
              <tr>
                <th className="px-3 py-2.5">Ref</th>
                <th className="px-3 py-2.5">Proposal Section</th>
                <th className="px-3 py-2.5">Category</th>
                <th className="px-3 py-2.5">Status</th>
                <th className="px-3 py-2.5 text-right">Words</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {sortedReqs.map((r, idx) => {
                const draft = sectionMap.get(r.id);
                const isApproved = draft?.status === "approved";
                const words = draft?.wordCount || 0;
                return (
                  <tr key={r.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30">
                    <td className="px-3 py-2 font-mono text-slate-400 font-semibold">
                      {r.sectionRef || `${idx + 1}.0`}
                    </td>
                    <td className="px-3 py-2 font-medium text-slate-900 dark:text-white">
                      {r.title}
                    </td>
                    <td className="px-3 py-2 uppercase text-[10px] font-bold text-slate-400">
                      {r.category}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-block rounded-md px-1.5 py-0.2 text-[10px] font-bold uppercase ${
                          isApproved
                            ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300"
                            : draft
                              ? "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300"
                              : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400"
                        }`}
                      >
                        {draft ? draft.status : "unwritten"}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-slate-600 dark:text-slate-400">
                      {words.toLocaleString()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Dual Sign-Off Gate Card */}
      <div className="rounded-2xl border border-slate-200/80 bg-white p-6 shadow-2xs dark:border-slate-800 dark:bg-slate-900 space-y-5">
        <div>
          <h3 className="text-sm font-bold uppercase tracking-wider text-slate-900 dark:text-white flex items-center gap-2">
            <Award className="h-4 w-4 text-[#2ba5b5]" />
            <span>Dual Leadership Sign-Off Gate</span>
          </h3>
          <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
            Enforces technical and executive verification prior to official release and submission.
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          {/* Proposal Lead Sign-Off */}
          <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-4 dark:border-slate-800 dark:bg-slate-800/40 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300">
                1. Proposal Lead Sign-Off
              </span>
              {opportunity.leadSignOff ? (
                <span className="rounded-md bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-800 uppercase dark:bg-emerald-950 dark:text-emerald-300">
                  Signed
                </span>
              ) : (
                <span className="rounded-md bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-800 uppercase dark:bg-amber-950 dark:text-amber-300">
                  Pending
                </span>
              )}
            </div>

            <p className="text-xs text-slate-500 dark:text-slate-400">
              Certifies technical solution accuracy, scope alignment, and requirement coverage.
            </p>

            {opportunity.leadSignOff ? (
              <div className="rounded-lg bg-white p-3 border border-slate-200/80 dark:bg-slate-900 dark:border-slate-700/80 text-xs space-y-1">
                <div className="font-semibold text-slate-900 dark:text-white">
                  {opportunity.leadSignOff.signerName} ({opportunity.leadSignOff.signerEmail})
                </div>
                <div className="text-[11px] text-slate-400">
                  Signed on {new Date(opportunity.leadSignOff.signedAt).toLocaleString()}
                </div>
                {opportunity.leadSignOff.comments && (
                  <p className="italic text-slate-600 dark:text-slate-400 mt-1">
                    &ldquo;{opportunity.leadSignOff.comments}&rdquo;
                  </p>
                )}
                {!isSubmitted && (
                  <button
                    type="button"
                    onClick={() => handleRevokeSignOff("lead")}
                    className="mt-2 text-[11px] font-semibold text-rose-600 hover:text-rose-700 dark:text-rose-400"
                  >
                    Revoke Sign-Off
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                <input
                  type="text"
                  value={leadComments}
                  onChange={(e) => setLeadComments(e.target.value)}
                  placeholder="Optional lead verification comments..."
                  className="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-900 focus:border-[#2ba5b5] focus:outline-hidden dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                />
                <button
                  type="button"
                  onClick={handleLeadSignOff}
                  disabled={isSigningLead || !user}
                  className="btn-animate inline-flex items-center gap-1.5 rounded-lg bg-[#2ba5b5] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#238a97] disabled:opacity-50"
                >
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  <span>{isSigningLead ? "Signing..." : "Stamp Proposal Lead Sign-Off"}</span>
                </button>
              </div>
            )}
          </div>

          {/* Executive / Officer Sign-Off */}
          <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-4 dark:border-slate-800 dark:bg-slate-800/40 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300">
                2. Executive / Officer Sign-Off
              </span>
              {opportunity.executiveSignOff ? (
                <span className="rounded-md bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-800 uppercase dark:bg-emerald-950 dark:text-emerald-300">
                  Authorized
                </span>
              ) : (
                <span className="rounded-md bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-800 uppercase dark:bg-amber-950 dark:text-amber-300">
                  Pending
                </span>
              )}
            </div>

            <p className="text-xs text-slate-500 dark:text-slate-400">
              Certifies pricing terms, legal liability commitments, and binding contract authority.
            </p>

            {opportunity.executiveSignOff ? (
              <div className="rounded-lg bg-white p-3 border border-slate-200/80 dark:bg-slate-900 dark:border-slate-700/80 text-xs space-y-1">
                <div className="font-semibold text-slate-900 dark:text-white">
                  {opportunity.executiveSignOff.signerName} (
                  {opportunity.executiveSignOff.signerEmail})
                </div>
                <div className="text-[11px] text-slate-400">
                  Authorized on {new Date(opportunity.executiveSignOff.signedAt).toLocaleString()}
                </div>
                {opportunity.executiveSignOff.comments && (
                  <p className="italic text-slate-600 dark:text-slate-400 mt-1">
                    &ldquo;{opportunity.executiveSignOff.comments}&rdquo;
                  </p>
                )}
                {!isSubmitted && (
                  <button
                    type="button"
                    onClick={() => handleRevokeSignOff("executive")}
                    className="mt-2 text-[11px] font-semibold text-rose-600 hover:text-rose-700 dark:text-rose-400"
                  >
                    Revoke Authorization
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                <input
                  type="text"
                  value={execComments}
                  onChange={(e) => setExecComments(e.target.value)}
                  placeholder="Optional executive authorization comments..."
                  className="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-900 focus:border-[#2ba5b5] focus:outline-hidden dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                />
                <button
                  type="button"
                  onClick={handleExecSignOff}
                  disabled={isSigningExec || !user}
                  className="btn-animate inline-flex items-center gap-1.5 rounded-lg bg-[#2ba5b5] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#238a97] disabled:opacity-50"
                >
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  <span>{isSigningExec ? "Authorizing..." : "Stamp Executive Authorization"}</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Record Submission Modal */}
      <RecordSubmissionModal
        isOpen={isRecordModalOpen}
        onClose={() => setIsRecordModalOpen(false)}
        opportunity={opportunity}
        user={user}
      />
    </div>
  );
};
